import { consts } from '@allesauto/utils/consts';
import { Error20 } from '@carbon/icons-react';
import clsx from 'clsx';
import React, { ChangeEvent, FC } from 'react';

import './textarea.css';

export interface TextareaProps extends React.HTMLProps<HTMLTextAreaElement> {
  className?: string;
  darkBg?: boolean;
  value?: string;
  label?: string;
  name?: string;
  placeholder?: string;
  maxHeight?: number;
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FormEvent<HTMLTextAreaElement>) => void;
  errorMessage?: string | boolean;
  tradeInWidget?: boolean;
}

// eslint-disable-next-line react/display-name
const Textarea: FC<TextareaProps> = (props: TextareaProps) => {
  const {
    className: customClassName = '',
    label = '',
    placeholder = '',
    value = '',
    darkBg = false,
    maxHeight = 200,
    onChange,
    onBlur,
    errorMessage,
    tradeInWidget = false,
  } = props;

  const {
    PDP: {
      CONTACT_DEALER_FORM: { TEXTAREA_CHARACTER_LIMIT },
    },
  } = consts;

  const className = {
    textareaContainer: clsx(
      'min-w-0 px-3px py-[2px] relative ring-1 ring-inset rounded-default w-full focus-within:ring-[3px] hover:ring-[3px]',
      darkBg && 'ring-white',
      !darkBg && 'ring-purple80 monochrome:ring-black',
      errorMessage && '!ring-red60 !ring-[3px]',
      customClassName,
      tradeInWidget && 'text-black ring-black'
    ),
    textarea: clsx(
      `base-textarea-anim bg-transparent border-0 font-weight-bold min-h-[120px] min-w-0 outline-none p-20px placeholder:font-weight-normal resize-none w-full lg:resize-y`,
      darkBg &&
        'text-white focus:placeholder-lightPurple monochrome:focus:placeholder-grey60 placeholder-transparent',
      !darkBg &&
        label &&
        'text-purple80 focus:placeholder-lightPurple monochrome:text-black monochrome:focus:placeholder-grey60 placeholder-transparent',
      !darkBg &&
        !label &&
        'text-purple80 placeholder-purple80 focus:placeholder-lightPurple monochrome:text-black monochrome:placeholder-black monochrome:focus:placeholder-grey60',
      errorMessage &&
        !label &&
        '!text-red60 focus:!placeholder-red60 !placeholder-red60 mb-5px',
      errorMessage &&
        label &&
        '!text-red60 placeholder-transparent focus:!placeholder-red60 mb-5px',
      tradeInWidget && 'focus:placeholder-grey60 text-black placeholder-black'
    ),
    inputError: clsx(
      '-bottom-[20px] absolute left-0 text-p3 text-red60 w-full'
    ),
    errorIcon: clsx('absolute right-20px text-red60 top-[15px]'),
    labelStyle: clsx(
      'absolute duration-300 leading-5 left-20px px-3px text-p1 top-20px',
      darkBg && ' bg-purple80 text-white monochrome:bg-black',
      !darkBg && 'bg-white text-purple80 monochrome:text-black',
      errorMessage && '!text-red60'
    ),
  };

  const { textareaContainer, textarea, labelStyle } = className;

  return (
    <div className={textareaContainer}>
      <textarea
        aria-label={label}
        name={props.name || label}
        className={textarea}
        placeholder={placeholder}
        value={value}
        maxLength={TEXTAREA_CHARACTER_LIMIT}
        onChange={onChange}
        onBlur={onBlur}
        style={{ maxHeight: `${maxHeight}px` }}
      />
      {label && (
        <label htmlFor={props.name} className={labelStyle}>
          {label}
        </label>
      )}
      {typeof errorMessage === 'string' && errorMessage && (
        <>
          <Error20 className={className.errorIcon} />
          <span className={className.inputError}>{errorMessage}</span>
        </>
      )}
    </div>
  );
};

export default Textarea;
